import React, { useEffect, useState } from 'react';
import { useApi } from '../../hooks/useApi';

interface ContractorLicense {
  id: string;
  userId: string;
  company: string;
  licenseNumber: string;
  licenseState: string;
  licenseExpiryDate: string;
  licenseVerified: boolean;
  licenseVerifiedAt: string | null;
  user: {
    email: string;
  };
}

const LicenseVerificationWorkflow: React.FC = () => {
  const [contractors, setContractors] = useState<ContractorLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'verified' | 'expired'>('all');
  const { get, post } = useApi();

  useEffect(() => {
    fetchContractors();
  }, [filter]);

  const fetchContractors = async () => {
    try {
      setLoading(true);
      const response = await get('/api/admin/contractors/license-verification');
      if (response.success) {
        setContractors(response.data);
      } else {
        setError(response.message || 'Failed to fetch contractors');
      }
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyLicense = async (contractorId: string, verified: boolean) => {
    try {
      const response = await post('/api/admin/contractors/license-verify', {
        contractorId,
        verified
      });

      if (response.success) {
        // Update the contractor in the local state
        setContractors(prev => 
          prev.map(contractor => 
            contractor.id === contractorId 
              ? { 
                  ...contractor, 
                  licenseVerified: verified,
                  licenseVerifiedAt: verified ? new Date().toISOString() : null
                }
              : contractor
          )
        );
      } else {
        setError(response.message || 'Failed to verify license');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify license');
    }
  };

  const filteredContractors = contractors.filter(contractor => {
    switch (filter) {
      case 'pending':
        return !contractor.licenseVerified && contractor.licenseNumber;
      case 'verified':
        return contractor.licenseVerified;
      case 'expired':
        return contractor.licenseExpiryDate && new Date(contractor.licenseExpiryDate) < new Date();
      default:
        return true;
    }
  });

  const isLicenseExpired = (expiryDate: string) => {
    return new Date(expiryDate) < new Date();
  };

  const getStatusColor = (contractor: ContractorLicense) => {
    if (!contractor.licenseNumber) return 'bg-gray-100 text-gray-800';
    if (contractor.licenseVerified) return 'bg-green-100 text-green-800';
    if (isLicenseExpired(contractor.licenseExpiryDate)) return 'bg-red-100 text-red-800';
    return 'bg-yellow-100 text-yellow-800';
  };

  const getStatusText = (contractor: ContractorLicense) => {
    if (!contractor.licenseNumber) return 'No License';
    if (contractor.licenseVerified) return 'Verified';
    if (isLicenseExpired(contractor.licenseExpiryDate)) return 'Expired';
    return 'Pending Verification';
  };

  if (loading) return <div className="p-6">Loading license verification data...</div>;
  if (error) return <div className="p-6 text-red-500">Error: {error}</div>;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">License Verification Workflow</h2>
        <p className="text-gray-600">Manage contractor license verification and compliance</p>
      </div>

      {/* Filter Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg w-fit">
          {[
            { key: 'all', label: 'All', count: contractors.length },
            { key: 'pending', label: 'Pending', count: contractors.filter(c => !c.licenseVerified && c.licenseNumber).length },
            { key: 'verified', label: 'Verified', count: contractors.filter(c => c.licenseVerified).length },
            { key: 'expired', label: 'Expired', count: contractors.filter(c => c.licenseExpiryDate && isLicenseExpired(c.licenseExpiryDate)).length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setFilter(tab.key as any)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === tab.key
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
      </div>

      {/* Contractors Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contractor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  License Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredContractors.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    No contractors found for the selected filter.
                  </td>
                </tr>
              ) : (
                filteredContractors.map((contractor) => (
                  <tr key={contractor.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {contractor.company || 'Contractor'}
                        </div>
                        <div className="text-sm text-gray-500">{contractor.user.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contractor.licenseNumber ? (
                        <div>
                          <div className="text-sm text-gray-900">
                            {contractor.licenseNumber}
                          </div>
                          <div className="text-sm text-gray-500">
                            {contractor.licenseState}
                          </div>
                          <div className="text-sm text-gray-500">
                            Expires: {new Date(contractor.licenseExpiryDate).toLocaleDateString()}
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">No license submitted</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contractor)}`}>
                        {getStatusText(contractor)}
                      </span>
                      {contractor.licenseVerifiedAt && (
                        <div className="text-xs text-gray-500 mt-1">
                          Verified: {new Date(contractor.licenseVerifiedAt).toLocaleDateString()}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {contractor.licenseNumber && !contractor.licenseVerified && (
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleVerifyLicense(contractor.id, true)}
                            className="text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                          >
                            Verify
                          </button>
                          <button
                            onClick={() => handleVerifyLicense(contractor.id, false)}
                            className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {contractor.licenseVerified && (
                        <button
                          onClick={() => handleVerifyLicense(contractor.id, false)}
                          className="text-red-600 hover:text-red-900 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                        >
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-gray-900">{contractors.length}</div>
          <div className="text-sm text-gray-500">Total Contractors</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-yellow-600">
            {contractors.filter(c => !c.licenseVerified && c.licenseNumber).length}
          </div>
          <div className="text-sm text-gray-500">Pending Verification</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-green-600">
            {contractors.filter(c => c.licenseVerified).length}
          </div>
          <div className="text-sm text-gray-500">Verified</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border">
          <div className="text-2xl font-bold text-red-600">
            {contractors.filter(c => c.licenseExpiryDate && isLicenseExpired(c.licenseExpiryDate)).length}
          </div>
          <div className="text-sm text-gray-500">Expired Licenses</div>
        </div>
      </div>
    </div>
  );
};

export default LicenseVerificationWorkflow;
